import React, { memo, useState, useEffect, useMemo, useCallback } from 'react';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { useDispatch } from 'react-redux';
import moment from 'moment-timezone';
import classNames from 'classnames';
import Sticky from 'react-sticky-el';
import xhr from '../../utils/xhr';
import Utils from '../../utils/utils';
import { showNotification } from '../../redux/notification';
import PERMISSION from '../../constants/Permissions';

import Button from '../../components/button/Button';
import WarningIcon from '../../components/icons/WarningIcon';
import BaseModal from '../../components/modals/BaseModal/BaseModal';
import BonusModal from '../../components/modals/BonusModal';
import GenericPermissionsModal from '../../components/modals/GenericPermissionsModal';
import EditWageModal from '../../components/modals/EditWageModal';
import ConfirmationModal from '../../components/modals/ConfirmationModal';
import CancelShiftModal from '../../components/modals/CancelShiftModal';
import VendorWarningModal from '../../components/modals/VendorWarningModal';
import InviteCaregiverModal from '../../components/modals/CaregiverShiftActionModals/InviteCaregiverModal';

import AddSlots from './daily-schedule/AddSlots';
import AddShiftsButton from './daily-schedule/AddShiftsButton';
import UnitAssignment from './UnitAssignment/UnitAssignment';
import GuaranteeSlotsModal from './GuaranteeShift/GuaranteeSlotsModal';
import { GLOBAL_CALENDAR_SHIFT_TYPES, SHIFT_STATUS } from '../../constants';
import GuaranteedCheckbox from './GuaranteeShift/GuaranteedCheckbox';
import ShiftSlot from './ShiftSlot/ShiftSlot';
import AssignCaregiverModal from '../../components/modals/CaregiverShiftActionModals/AssignCaregiver/AssignCaregiverModal';
import CancelFlexShiftModal from '../../components/modals/CancelFlexShiftModal';
import SingleActionModal from '../../components/modals/SingleActionModal';
import RouteSlotModal from '../../components/modals/RouteSlotModal/RouteSlotModal';
import ShiftTypeBadge from './ShiftTypeBadge';
import { useFacilityFloatSlotMutation, useRouteSlotMutation } from '../../redux/globalOverview';
import { loadFeed } from '../../redux/feed';
import { SHIFT_ROUTES, SHIFT_TYPES } from '../../constants/ShiftStatus';
import FacilityFloatModal from '../../components/modals/FacilityFloatModal/FacilityFloatModal';
import facilityInfoIcon from '../../assets/images/facility-info-icon.svg';
import orientationIcon from '../../assets/images/compass.svg';
import useMediaQuery, { MEDIUM_SCREEN_WIDTH_QUERY } from '../../components/hooks/useMediaQuery';
import OnCallBadge from './OnCallBadge';

const Shift = (props) => {
	const dispatch = useDispatch();
	const {
		units = [],
		data: shift,
		user,
		isFilterByCaregiver,
		hideLabel,
		allSlotsAreGuaranteed,
		shiftsForAllDay
	} = props;
	const [showConfirmConfirmation, setShowConfirmConfirmation] = useState(false);
	const [showCancelConfirmation, setShowCancelConfirmation] = useState(false);
	const [showGuaranteedModal, setShowGuaranteedModal] = useState(false);
	const [showAddSlotVendorWarningModal, setShowAddSlotVendorWarningModal] = useState(false);
	const [showConfirmShiftVendorWarningModal, setShowConfirmShiftVendorWarningModal] = useState(false);
	const [showCancelShiftWithCaregiverVendorWarningModal, setShowCancelShiftWithCaregiverVendorWarningModal] = useState(
		false
	);
	const [showInviteCaregiverModal, setShowInviteCaregiverModal] = useState(false);
	const [isGuaranteed, setIsGuaranteed] = useState(false);
	const [showSlotsModal, setShowSlotsModal] = useState(false);
	const [showFlexSlotsModal, setShowFlexSlotsModal] = useState(false);
	const [shiftSelectedToAddSlots, setShiftSelectedToAddSlots] = useState({});
	const [flexShiftSelectedToAddSlots, setFlexShiftSelectedToAddSlots] = useState({});
	const [numberOfSlots, setNumberOfSlots] = useState(1);
	const [showBonusPermissionsModal, setShowBonusPermissionsModal] = useState(false);
	const [showEditWagePermissionModal, setShowEditWagePermissionModal] = useState(false);
	const [showEditWageModal, setShowEditWageModal] = useState(false);
	const [showBonusModal, setShowBonusModal] = useState(false);
	const [showCancelModal, setShowCancelModal] = useState(false);
	const [showCancelFlex, setShowCancelFlex] = useState(false);
	const [selectedSlotToCancel, setSelectedSlotToCancel] = useState({});
	const [showConfirmModalContractCancellation, setShowConfirmModalContractCancellation] = useState(false);
	const [showAssignCaregiverModal, setShowAssignCaregiverModal] = useState(false);
	const [showRouteSlotModal, setShowRouteSlotModal] = useState(false);
	const [cancelModalErrorMessage, setCancelModalErrorMessage] = useState(null);
	const [onDemandShiftId, setOnDemandShiftId] = useState(shift.routeData[GLOBAL_CALENDAR_SHIFT_TYPES.ON_DEMAND].id);
	const [isRecurring, setIsRecurring] = useState(shift.routeData[GLOBAL_CALENDAR_SHIFT_TYPES.ON_DEMAND].isRecurring);
	const [showUnitAssignmentModal, setShowUnitAssignmentModal] = useState(false);
	const [slotsToGuarantee, setSlotsToGuarantee] = useState([]);
	const [unitAssignmentSlot, setUnitAssignmentSlot] = useState({});
	const [slotToConfirm, setSlotToConfirm] = useState(null);
	const [showRefreshModal, setShowRefreshModal] = useState(false);
	const [slotToAssign, setSlotToAssign] = useState({});
	const [slotToRoute, setSlotToRoute] = useState({});
	const [slotToFloat, setSlotToFacilityFloat] = useState({});
	const [currentRequestType, setCurrentRequestType] = useState();
	const [showFacilityFloatModal, setShowFacilityFloatModal] = useState(false);
	const slotRefs = {};
	const isMobile = useMediaQuery(MEDIUM_SCREEN_WIDTH_QUERY);

	const [routeSlot] = useRouteSlotMutation();
	const [facilityFloatSlot] = useFacilityFloatSlotMutation();

	const hasToggleGuaranteePermission = useMemo(
		() => Utils.isAuthorizedExact(PERMISSION.GUARANTEE, props.user.permissions),
		[props.user.permissions]
	);

	useEffect(() => {
		if (shift.routeData[GLOBAL_CALENDAR_SHIFT_TYPES.ON_DEMAND].id) {
			setOnDemandShiftId(shift.routeData[GLOBAL_CALENDAR_SHIFT_TYPES.ON_DEMAND].id);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [shift.routeData]);

	const getEndUserMessage = (e) => {
		const res = e.response;
		const validationError = res && res.data && res.data.validationError;
		if (validationError) {
			if (res && res.data && res.data.validationError) {
				const errorMessage = res.data.errors[0].message;
				if (errorMessage) {
					return errorMessage;
				}
			}
		}
		return null;
	};

	useEffect(() => {
		const openSlots = shift.slots.filter((slot) => !slot.caregiver_id && !slot.is_cancelled && !slot.is_flex);
		const allOnDemandOpenSlots = openSlots.filter((slot) => !slot.is_flex);
		const allOpenSlotsGuaranteed =
			allOnDemandOpenSlots.length > 0 && allOnDemandOpenSlots.every((slot) => slot.is_guaranteed);
		const _isGuaranteed = allOnDemandOpenSlots.length > 0 && allOpenSlotsGuaranteed;
		setIsGuaranteed(_isGuaranteed);
		shift.routeData[GLOBAL_CALENDAR_SHIFT_TYPES.ON_DEMAND].isGuaranteed = allOpenSlotsGuaranteed;
	}, [shift.slots, shift.routeData]);

	const confirm = async () => {
		setShowConfirmShiftVendorWarningModal(false);
		props.setIsLoading(true);
		try {
			if (slotToConfirm.is_flex) {
				await xhr.request('PUT', `/flex/slot/${slotToConfirm.slot_id}/confirm`, {});
			} else {
				await xhr.request('PUT', `/slot/${slotToConfirm.slot_id}/confirm`, {});
			}
			props.updateShiftStatus(slotToConfirm.shift_date, slotToConfirm.slot_id, 'Confirmed');
			setSlotToConfirm(null);
		} catch (e) {
			if (e.response.status !== 403) {
				dispatch(showNotification('error', getEndUserMessage(e) || 'Failed to confirm shift. Please try again!'));
				props.fetchData();
			}
		} finally {
			props.setIsLoading(false);
			props.fetchData();
		}
	};

	const updateIsGuaranteedSlots = async (slots, newGuaranteedStatus) => {
		props.setIsLoading(true);
		const [shiftData] = slots;
		const slotValues = slots.reduce((acc, slot) => {
			acc[slot.slot_id] = newGuaranteedStatus;
			return acc;
		}, {});
		try {
			await xhr.request('PUT', `/shifts/${shiftData.shift_id}/slots/guaranteed`, {
				isRecurring: shiftData.isRecurring,
				slots: slotValues,
				shiftDate: moment.utc(shiftData.raw_start_time).format('YYYY-MM-DD')
			});
			props.fetchData();
			setShowGuaranteedModal(false);
			dispatch(showNotification('success', `Guarantee ${newGuaranteedStatus ? 'set' : 'removed'} successfully.`));
		} catch (e) {
			const errorMessage = getEndUserMessage(e) || 'Failed to update shift. Please try again!';
			dispatch(showNotification('error', errorMessage));
			if (e.response.status === 403) {
				setShowGuaranteedModal(false);
			}
		} finally {
			props.setIsLoading(false);
		}
	};

	const onConfirm = (slot) => {
		setShowConfirmConfirmation(true);
		setSlotToConfirm(slot);
	};

	const onCancel = (_selectedSlotToCancel) => {
		setShowConfirmModalContractCancellation(false);

		if (_selectedSlotToCancel && _selectedSlotToCancel.caregiver_id) {
			setShowCancelShiftWithCaregiverVendorWarningModal(true);
		} else {
			setShowCancelConfirmation(true);
		}
	};

	const withdrawContractCancellation = () => {
		setShowConfirmModalContractCancellation(false);
		setSelectedSlotToCancel({});
	};

	const onPrepareCancel = (slot) => {
		setSelectedSlotToCancel(slot);
		if (slot.is_flex) {
			setShowCancelFlex(true);
		} else if (slot.contract_id) {
			// for slot with contract we need to show additional modal
			setShowConfirmModalContractCancellation(true);
		} else {
			onCancel(slot);
		}
	};

	const onGuaranteedCheckboxChange = (slots = []) => {
		setSlotsToGuarantee(slots.filter((slot) => !slot.caregiver_id && !slot.is_cancelled));
		setShowGuaranteedModal(true);
	};

	const handleEditBonusModalButton = useCallback(() => {
		if (!Utils.isAuthorizedExact(PERMISSION.BONUS, props.user.permissions)) {
			setShowBonusPermissionsModal(true);
		} else {
			setShowBonusModal(['EDIT', shift]);
		}
	}, [props.user.permissions, setShowBonusPermissionsModal, setShowBonusModal, shift]);

	const handleAddBonusModalButton = useCallback(() => {
		if (!Utils.isAuthorizedExact(PERMISSION.BONUS, props.user.permissions)) {
			setShowBonusPermissionsModal(true);
		} else {
			setShowBonusModal(['ADD', shift]);
		}
	}, [props.user.permissions, setShowBonusPermissionsModal, setShowBonusModal, shift]);

	const handleEditWageModalButton = useCallback(() => {
		if (!Utils.isAuthorizedExact(PERMISSION.EDIT_WAGE, props.user.permissions)) {
			setShowEditWagePermissionModal(true);
		} else {
			setShowEditWageModal(true);
		}
	}, [props.user.permissions, setShowEditWagePermissionModal, setShowEditWageModal]);

	const updateFacilityPayableBonus = async (bonusAmount, eventCode) => {
		props.setIsLoading(true);
		try {
			await xhr.request('PUT', `/shift/${onDemandShiftId}/facility-bonus`, {
				shiftId: onDemandShiftId,
				eventCode,
				facilityPayableBonus: bonusAmount,
				isRecurring,
				shiftDate: moment.utc(shift.raw_start_time).format('YYYY-MM-DD')
			});
			props.fetchData();
			dispatch(showNotification('success', 'Bonus updated successfully'));
		} catch (e) {
			if (e.response.status !== 403) {
				let userMessage = getEndUserMessage(e);
				if (userMessage && userMessage.indexOf('is not empty') > 0) {
					userMessage = 'Data has changed. Please refresh the page.';
				}
				dispatch(showNotification('error', userMessage || 'Failed to edit bonus. Please try again!'));
			}
		} finally {
			props.setIsLoading(false);
			setShowBonusModal(false);
		}
	};

	const showModalToAddSlots = (shiftForModal) => {
		setShowAddSlotVendorWarningModal(false);
		if (currentRequestType === SHIFT_ROUTES.FLEX) {
			setFlexShiftSelectedToAddSlots({ ...shiftForModal });
			setShowFlexSlotsModal(true);
		} else {
			setShiftSelectedToAddSlots({ ...shiftForModal, shift_guaranteed: isGuaranteed });
			setShowSlotsModal(true);
		}
	};

	const hideSlotsModal = () => {
		setShowSlotsModal(false);
		setShiftSelectedToAddSlots({});
		setCurrentRequestType();
	};

	const hideFlexSlotsModal = () => {
		setShowFlexSlotsModal(false);
		setShiftSelectedToAddSlots({});
		setCurrentRequestType();
	};

	const handleInviteButton = () => {
		setShowInviteCaregiverModal(true);
	};

	const handleAddFlexWorkerButton = (slot) => {
		setSlotToAssign(slot);
		setShowAssignCaregiverModal(true);
	};

	const handleRouteSlotButton = (slot) => {
		setSlotToRoute(slot);
		setShowRouteSlotModal(true);
	};

	const handleFacilityFloatSlotButton = (shiftToFloat) => {
		const { slot: currentSlot } = shiftToFloat;
		const shiftToSlot = {
			shift: {
				resource_type: currentSlot.resource_type,
				facility_name: currentSlot.facility_name
			},
			slot: currentSlot
		};
		setSlotToFacilityFloat(shiftToSlot);
		setShowFacilityFloatModal(true);
	};
	const handleCloseInviteCaregiverModal = () => {
		setShowInviteCaregiverModal(false);
	};

	const handleCloseAssignCaregiverModal = () => {
		setShowAssignCaregiverModal(false);
		setSlotToAssign({});
	};

	const handleOnAssign = (notificationType, message) => {
		dispatch(showNotification(notificationType, message));
		setShowAssignCaregiverModal(false);
		setSlotToAssign({});
		props.fetchData();
	};

	/**
	 * @param {String} notificationType
	 * @param {String} message
	 */
	const handleSendInvitation = (notificationType, message) => {
		dispatch(showNotification(notificationType, message));
		setShowInviteCaregiverModal(false);
	};

	const currentFacility = useMemo(() => user.facilitiesEnabled.find((facility) => facility.id === shift.facilityId), [
		user.facilitiesEnabled,
		shift
	]);

	const requestNewSlots = async () => {
		setShowSlotsModal(false);
		props.setIsLoading(true);
		try {
			const shouldNotCreateShift = shift.shift_routes?.has(SHIFT_TYPES.ON_DEMAND);
			if (!shouldNotCreateShift) {
				const postUrl = `/shifts`;
				const payload = {
					facilityId: currentFacility.value,
					shiftsData: [
						{
							structure_id: shiftSelectedToAddSlots.shift_structure_id,
							slots: numberOfSlots,
							unit_id: null,
							start_date: moment
								.tz(shiftSelectedToAddSlots.start_time_at_utc, shift.facility_timezone)
								.format('YYYY-MM-DD')
						}
					]
				};
				await xhr.request('POST', postUrl, payload);
			} else {
				await xhr.request('POST', `/slots`, {
					facilityId: shiftSelectedToAddSlots.facilityId,
					shiftId: onDemandShiftId,
					numberOfSlots,
					isRecurring,
					shiftDate: moment.utc(shiftSelectedToAddSlots.raw_start_time).format('YYYY-MM-DD')
				});
			}

			props.fetchData();
			dispatch(showNotification('success', 'Created successfully'));
		} catch (e) {
			if (e.response.status !== 403) {
				let userMessage = getEndUserMessage(e);
				if (userMessage && userMessage.indexOf('is not empty') > 0) {
					userMessage = 'Data has changed. Please refresh the page.';
				}
				dispatch(showNotification('error', userMessage || 'Failed to add shifts. Please try again!'));
			}
		} finally {
			props.setIsLoading(false);
			setShiftSelectedToAddSlots({});
			setNumberOfSlots(1);
		}
	};

	const requestNewFlexSlots = async () => {
		setShowFlexSlotsModal(false);
		props.setIsLoading(true);
		try {
			const postUrl = `/flex/facility/${currentFacility.value}/shifts`;
			const payload = [
				{
					structure_id: flexShiftSelectedToAddSlots.shift_structure_id,
					slots: numberOfSlots,
					unit_id: null,
					start_date: moment
						.tz(flexShiftSelectedToAddSlots.start_time_at_utc, shift.facility_timezone)
						.format('YYYY-MM-DD')
				}
			];

			await xhr.request('POST', postUrl, payload);
			dispatch(showNotification('success', 'Created successfully'));
			props.fetchData();
			if (props.isSingleSlotView) {
				props.setIsLoading(false);
			}
		} catch (e) {
			if (e.response.status !== 403) {
				let userMessage = getEndUserMessage(e);
				if (userMessage && userMessage.indexOf('is not empty') > 0) {
					userMessage = 'Data has changed. Please refresh the page.';
				}
				dispatch(showNotification('error', userMessage || 'Failed to add shifts. Please try again!'));
			}
		} finally {
			props.setIsLoading(false);
			setShiftSelectedToAddSlots({});
			setNumberOfSlots(1);
		}
	};

	const requestHybridSlots = async () => {
		setShowSlotsModal(false);
		props.setIsLoading(true);
		try {
			if (!onDemandShiftId) {
				const postUrl = `/hybrid/facility/${currentFacility.value}/shifts`;
				const payload = [
					{
						structure_id: shiftSelectedToAddSlots.shift_structure_id,
						slots: numberOfSlots,
						unit_id: null,
						start_date: moment
							.tz(shiftSelectedToAddSlots.start_time_at_utc, shift.facility_timezone)
							.format('YYYY-MM-DD')
					}
				];
				await xhr.request('POST', postUrl, payload);
			} else {
				await xhr.request('POST', `/hybrid/slots`, {
					shiftId: onDemandShiftId,
					numberOfSlots,
					isRecurring,
					shiftDate: moment.utc(shiftSelectedToAddSlots.raw_start_time).format('YYYY-MM-DD')
				});
			}

			props.fetchData();
			dispatch(showNotification('success', 'Created successfully'));
		} catch (e) {
			if (e.response.status !== 403) {
				let userMessage = getEndUserMessage(e);
				if (userMessage && userMessage.indexOf('is not empty') > 0) {
					userMessage = 'Data has changed. Please refresh the page.';
				}
				dispatch(showNotification('error', userMessage || 'Failed to add shifts. Please try again!'));
			}
		} finally {
			props.setIsLoading(false);
			setShiftSelectedToAddSlots({});
			setNumberOfSlots(1);
		}
	};
	const updateSlotArrivalStatus = async (slotId, arrivalStatus) => {
		props.setIsLoading(true);
		try {
			await xhr.request('PUT', `/slot/${slotId}/arrival-status`, {
				arrivalStatus
			});
			props.fetchData();
			dispatch(showNotification('success', 'Arrival status updated successfully'));
		} catch (e) {
			if (e.response.status !== 403) {
				let userMessage = getEndUserMessage(e);
				if (userMessage && userMessage.indexOf('is not empty') > 0) {
					userMessage = 'Data has changed. Please refresh the page.';
				}
				dispatch(showNotification('error', userMessage || 'Failed to update arrival status shifts. Please try again!'));
			}
		} finally {
			props.setIsLoading(false);
		}
	};

	const getRowColor = (rowIndex) => (rowIndex % 2 === 0 ? 'even' : 'odd');

	const computeFilledSlots = (slots) => {
		let filledSlots = 0;
		let totalSlots = 0;
		slots.forEach((slot) => {
			if ((slot.caregiver_id && slot.is_guaranteed && slot.is_cancelled) || slot.is_cancelled) {
				// A slot that has been already picked up, guaranteed and cancelled by facility should not be taken into account neither on the filled nor on the totalSlots.
				return;
			}
			if (
				(slot.caregiver_id && !slot.is_cancelled) ||
				(slot.integrated_shift_expired && slot.slot_status !== SHIFT_STATUS.CONFIRMED)
			) {
				filledSlots += 1;
			}
			totalSlots += 1;
		});
		return [filledSlots, totalSlots];
	};

	const getShiftRatio = (shiftForRatio) => {
		const [filledSlots, totalSlots] = computeFilledSlots(shiftForRatio.slots);
		return `${filledSlots} of ${totalSlots}`;
	};

	const preventGuaranteeOrBonusUpdates = !!(shift.slots.length === 1 && shift.slots[0].caregiver_id);

	const handleCancelShiftModalButton = async (
		cancelationReason,
		needsReplacement,
		isLateCancelation,
		acknowledgmentAccepted,
		agencyName,
		isNonClaimedSlot = false
	) => {
		props.setIsLoading(true);

		const updateSlotId = selectedSlotToCancel.isRecurring
			? selectedSlotToCancel.shift_id
			: selectedSlotToCancel.slot_id;
		// The id passed to the cancel function for recurring shifts should be the shift.id instead of the slot.id

		let slotRelated;

		if (!selectedSlotToCancel.isRecurring) {
			slotRelated = shift.slots ? shift.slots.find((ss) => Number(ss.slot_id) === Number(updateSlotId)) : null;
			if (!slotRelated) {
				// This should not happen. Covering against bad or missing reference for slot id.
				dispatch(showNotification('error', 'Failed to cancel shift. Please try again!'));
				props.setIsLoading(false);
				return;
			}
		}

		try {
			if (selectedSlotToCancel.is_flex) {
				await xhr.request('PUT', `/flex/slot/${updateSlotId}/cancel`, {
					cancelationReason,
					needsReplacement,
					isLateCancelation,
					extraInfo: null
				});
			} else {
				await xhr.request('PUT', `/slot/${updateSlotId}/cancel`, {
					facilityId: currentFacility.id,
					isGuaranteed: !!selectedSlotToCancel.is_guaranteed,
					isRecurring: !!selectedSlotToCancel.isRecurring,
					shiftDate: moment.utc(selectedSlotToCancel.raw_start_time).format('YYYY-MM-DD'),
					cancelationReason,
					needsReplacement,
					isLateCancelation,
					guaranteeAcknowledge: acknowledgmentAccepted,
					lateCancelationAcknowledge: acknowledgmentAccepted,
					extraInfo: agencyName ? { reason: agencyName } : null,
					facilityBillableCancellationHours: currentFacility.facility_billable_cancellation_hours,
					facilityWindowCancellationHours: currentFacility.facility_window_cancellation_hours
				});
			}

			dispatch(showNotification('success', 'Shift cancelled successfully.'));
			if (props.fromGlobalOverview) {
				props.history.goBack();
			} else {
				props.fetchData();
				if (isNonClaimedSlot) {
					// non-claimed slot needs to call updateShiftStatus and update the slot status to canceled
					props.updateShiftStatus(selectedSlotToCancel.shift_date, updateSlotId, 'Canceled');
					setShowCancelConfirmation(false);
				} else {
					setShowCancelModal(false);
					setSelectedSlotToCancel({});
					setShowCancelFlex(false);
				}
			}
		} catch (e) {
			if (e?.response?.status !== 403) {
				const error = e?.response?.data?.errors[0];
				if (error?.field === 'uber_ride_status') {
					setCancelModalErrorMessage(error?.message);
				} else {
					dispatch(showNotification('error', getEndUserMessage(e) || 'Failed update shift. Please try again!'));
				}
			}
		} finally {
			props.setIsLoading(false);
		}
	};

	const handleFlexShiftCancel = async (cancelationReason, needsReplacement, isClaimedSlot = false) => {
		props.setIsLoading(true);

		const updateSlotId = selectedSlotToCancel.slot_id;

		try {
			await xhr.request('PUT', `/flex/slot/${updateSlotId}/cancel`, {
				cancelationReason,
				needsReplacement,
				extraInfo: null
			});

			dispatch(showNotification('success', 'Shift cancelled successfully.'));

			if (props.fromGlobalOverview) {
				props.history.goBack();
			} else {
				props.fetchData();
				if (!isClaimedSlot) {
					// non-claimed slot needs to call updateShiftStatus and update the slot status to canceled
					props.updateShiftStatus(selectedSlotToCancel.shift_date, updateSlotId, 'Canceled');
				} else {
					setSelectedSlotToCancel({});
				}
				setShowCancelFlex(false);
			}
		} catch (e) {
			if (e?.response?.status !== 403) {
				const { field, message: errorMessage } = Utils.handleNetworkError(e);
				if (field === 'status_code') {
					setShowCancelFlex(false);
					setShowRefreshModal(true);
				}
				if (field === 'outcome_code') {
					dispatch(showNotification('error', errorMessage));
					setShowCancelFlex(false);
					props.fetchData();
				} else {
					dispatch(showNotification('error', errorMessage || 'Failed update shift. Please try again!'));
				}
			}
		} finally {
			props.setIsLoading(false);
		}
	};

	const OnRouteSlot = async (slot) => {
		try {
			props.setIsLoading(true);
			await routeSlot({ slot }).unwrap();
			dispatch(showNotification('success', 'Shift has been successfully routed.'));
		} catch (reopenError) {
			const fallbackMessage = `There was an error routing the shift.`;
			dispatch(showNotification('error', Utils.parseRTKQueryValidationError(reopenError, fallbackMessage)));
		} finally {
			props.setIsLoading(false);
			props.fetchData();
			setShowRouteSlotModal(false);
		}
	};

	const onCloseCancelShiftModal = (isNonClaimedSlot) => {
		if (isNonClaimedSlot) {
			setShowCancelConfirmation(false);
		} else {
			setShowCancelModal(false);
		}

		if (cancelModalErrorMessage) {
			setCancelModalErrorMessage(null);
		}
	};

	const onFacilityFloatSlot = async (targetFacilityId, caregiverId, structureId, unitId) => {
		try {
			props.setIsLoading(true);
			const { slot } = slotToFloat;
			await facilityFloatSlot({ slot, targetFacilityId, caregiverId, structureId, unitId }).unwrap();
			dispatch(showNotification('success', 'Shift has been successfully floated.'));
			setShowFacilityFloatModal(false);
		} catch (error) {
			if (!Array.isArray(error?.data?.errors) || error.data.errors[0]?.field === 'end_time') {
				const fallbackMessage = `There was an error floating the shift.`;
				setShowFacilityFloatModal(false);
				dispatch(showNotification('error', Utils.parseRTKQueryValidationError(error, fallbackMessage)));
				return {};
			}
			return error;
		} finally {
			props.fetchData();
			props.setIsLoading(false);
		}
		return {};
	};

	const sortSlots = (slots) => {
		if (!slots) return [];

		const slotGroups = {
			filled_flex: [],
			filled_ondemand: [],
			pending: [],
			confirmed: [],
			open_flex: [],
			open_ondemand: [],
			open_hybrid: [],
			rest: []
		};

		slots.forEach((slot) => {
			const isFilled = !!slot.caregiver_name;
			const isConfirmed = !!slot.slot_status;
			const isFlex = !!slot.is_flex;
			const isHybrid = !isFlex && !!slot.is_hybrid;

			if (isFilled && isFlex) {
				slotGroups.filled_flex.push(slot);
			} else if (isFilled && !isConfirmed && !isFlex) {
				slotGroups.pending.push(slot);
			} else if (isFilled && isConfirmed && !isFlex) {
				slotGroups.confirmed.push(slot);
			} else if (!isFilled && isHybrid) {
				slotGroups.open_hybrid.push(slot);
			} else if (!isFilled && isFlex) {
				slotGroups.open_flex.push(slot);
			} else if (!isFilled && !isFlex) {
				slotGroups.open_ondemand.push(slot);
			} else {
				slotGroups.rest.push(slot);
			}
		});

		const sortByCaregiverName = (arr) =>
			arr.sort((a, b) => (a.caregiver_name || '').localeCompare(b.caregiver_name || ''));

		return [
			...sortByCaregiverName(slotGroups.filled_flex),
			...sortByCaregiverName(slotGroups.filled_ondemand),
			...sortByCaregiverName(slotGroups.pending),
			...sortByCaregiverName(slotGroups.confirmed),
			...sortByCaregiverName(slotGroups.open_hybrid),
			...sortByCaregiverName(slotGroups.open_flex),
			...sortByCaregiverName(slotGroups.open_ondemand),
			...sortByCaregiverName(slotGroups.rest)
		];
	};

	const handleRequestShift = (shiftRequestType) => {
		setShowAddSlotVendorWarningModal(true);
		setCurrentRequestType(shiftRequestType);
	};

	const openOnDemandSlotsCount = useMemo(
		() => shift.slots?.filter((slot) => !slot.is_flex && !slot.caregiver_id).length,
		[shift.slots]
	);

	const editBonusWageButton = useCallback(
		(facilityPayableBonus) =>
			props.isFixedMarkup ? (
				<Button
					exactPermission={PERMISSION.EDIT}
					className="edit-wage add-bonus-button text-nowrap"
					disabled={!openOnDemandSlotsCount}
					onClick={handleEditWageModalButton}
					title="Edit Wage"
				/>
			) : (
				[
					!facilityPayableBonus && (
						<Button
							exactPermission={PERMISSION.BONUS}
							className="add-bonus-button text-nowrap"
							disabled={!openOnDemandSlotsCount}
							onClick={handleAddBonusModalButton}
							title="+ Add Bonus"
							key="btnAddBonus"
						/>
					),
					!!facilityPayableBonus && (
						<Button
							exactPermission={PERMISSION.BONUS}
							className="edit-bonus-button text-nowrap"
							disabled={!openOnDemandSlotsCount}
							onClick={handleEditBonusModalButton}
							title="Edit Bonus"
							key="btnEditBonus"
						/>
					)
				]
			),
		[
			props.isFixedMarkup,
			openOnDemandSlotsCount,
			handleEditWageModalButton,
			handleAddBonusModalButton,
			handleEditBonusModalButton
		]
	);
	const isShowingGuaranteedBadge = useMemo(() => {
		return (
			!isFilterByCaregiver && shift.shift_routes.has(GLOBAL_CALENDAR_SHIFT_TYPES.ON_DEMAND) && !props.fromGlobalOverview
		);
	}, [isFilterByCaregiver, shift, props.fromGlobalOverview]);

	const shouldShowRouteBadges =
		shift.shift_routes.has(GLOBAL_CALENDAR_SHIFT_TYPES.FLEX) || props.showOnDemandShiftTypeBadges;

	const shiftBadges = useMemo(() => {
		if (!props.fromGlobalOverview) return shift.shift_routes;

		const shouldIncludeFlex =
			shift.shift_routes.has(GLOBAL_CALENDAR_SHIFT_TYPES.FLEX) &&
			(props.shiftType === SHIFT_TYPES.HYBRID || props.shiftType === SHIFT_TYPES.FLEX);

		return shouldIncludeFlex
			? shift.shift_routes
			: [...shift.shift_routes].filter((sr) => sr !== GLOBAL_CALENDAR_SHIFT_TYPES.FLEX);
	}, [props.fromGlobalOverview, props.shiftType, shift.shift_routes]);

	const HybridSlotsRequestTitle = () => {
		return (
			<div className="request-shift-title">
				<span>Add a new hybrid shift</span>
				<OverlayTrigger
					className="tooltip-component"
					placement={isMobile ? 'bottom' : 'right'}
					delay={{ show: 100, hide: 400 }}
					overlay={
						<Tooltip
							delay={{ show: 150, hide: 400 }}
							className={classNames('info-facility-tooltip', { 'shift-bottom-placement': isMobile })}
						>
							Hybrid shifts are open to both Flex and ShiftMed professionals and are filled on a first come first serve
							basis. If you want to limit which group can claim, you can use the request On Demand or request Flex shift
							options.
						</Tooltip>
					}
				>
					<img className="tooltip-title" src={facilityInfoIcon} alt="info" />
				</OverlayTrigger>
			</div>
		);
	};

	const getRequestSlotTitle = () => {
		if (currentRequestType === SHIFT_ROUTES.HYBRID) {
			return HybridSlotsRequestTitle();
		}
		if (shift.isOnCall) {
			return 'Add a new On Call Shift';
		}
		return 'Add a new shift';
	};

	return [
		<div
			className={`col-sm-2 col-lg-2 col-xl-2 p-0 d-none d-sm-flex with-border shift-status-cell ${getRowColor(
				shift.shiftIndex
			)}`}
			key={`${shift.start_time}${shift.end_time}`}
		>
			<div className="col p-0 d-flex d-sm-flex shift-interval-cell">
				<Sticky
					className="w-100 sticky-class"
					scrollElement=".virtuoso-container"
					boundaryElement=".shift-status-cell > div"
					dontUpdateHolderHeightWhenSticky
					hideOnBoundaryHit={false}
					disabled={shift.slots.length < 3}
				>
					<div className="row no-gutters shift-info-container">
						<div className="col-sm-12 col-lg-12 col-xl-12  shift-block d-flex flex-column justify-content-around">
							{!isShowingGuaranteedBadge && shouldShowRouteBadges && (
								<div className="row align-items-left">
									<div className="d-flex flex-row align-items-center shift-info-badges gap-2">
										{Array.from(shiftBadges).map((route) => (
											<>
												<ShiftTypeBadge shiftType={route} key={`shift-type-badge-${route}`} />
											</>
										))}
										{shift.isOnCall && <OnCallBadge />}
									</div>
								</div>
							)}
							<div className="row no-gutters flex-column align-items-center">
								<div className="w-100 align-items-center justify-content-between">
									{isShowingGuaranteedBadge && (
										<div className="d-flex align-items-start justify-content-between w-100">
											{shouldShowRouteBadges && (
												<div className="d-flex flex-column align-items-start shift-info-badges">
													{Array.from(shiftBadges).map((route) => (
														<>
															<ShiftTypeBadge shiftType={route} key={`shift-type-badge-${route}`} />
														</>
													))}
													{shift.isOnCall && <OnCallBadge />}
												</div>
											)}
											<GuaranteedCheckbox
												onGuaranteedCheckboxChange={onGuaranteedCheckboxChange}
												shift={shift}
												preventGuaranteeOrBonusUpdates={preventGuaranteeOrBonusUpdates}
												className="guaranteed-shift d-sm-flex"
												isGuaranteed={isGuaranteed}
												isAllowedToToggle={hasToggleGuaranteePermission && !!openOnDemandSlotsCount}
												hideLabelText
											/>
										</div>
									)}
									{shift.shift_routes.has(GLOBAL_CALENDAR_SHIFT_TYPES.ON_DEMAND) && shift.is_orientation && (
										<OverlayTrigger
											placement="right"
											trigger={['hover', 'focus']}
											show
											overlay={
												<Tooltip delay={{ show: 150, hide: 400 }} className="orientation-tooltip">
													Orientation
												</Tooltip>
											}
										>
											<span className="orientation-block">
												<img className="orientation-icon" src={orientationIcon} alt="Orientation shift" />
												<span className="orientation-block-text">Orientation</span>
											</span>
										</OverlayTrigger>
									)}
								</div>

								<div className="shift-ratio resource-type">
									<span>{shift.slots[0].resource_type}</span>
								</div>
								{!isFilterByCaregiver && !hideLabel && (
									<div className="shift-ratio">
										<span>{getShiftRatio(shift)} filled</span>
									</div>
								)}
							</div>
						</div>
						{!isFilterByCaregiver && (
							<div className="col-sm-12 col-lg-12 col-xl-12 d-flex flex-column justify-content-around buttons-container">
								<div className="row no-gutters mt-4 add-buttons-column">
									<AddShiftsButton
										currentFacility={currentFacility}
										user={user}
										shift={shift}
										shiftType={props.shiftType}
										delinquentAccountReceivableStatus={props.delinquentAccountReceivableStatus}
										fromGlobalOverview={props.fromGlobalOverview}
										handleRequestShift={handleRequestShift}
										setShowAddSlotVendorWarningModal={setShowAddSlotVendorWarningModal}
									/>
									{shift.shift_routes.has(GLOBAL_CALENDAR_SHIFT_TYPES.ON_DEMAND) && (
										<div className="action-buttons">
											{editBonusWageButton(shift.facilityPayableBonus)}
											{Utils.isAuthorizedExact(PERMISSION.INVITE, props.user.permissions) && (
												<Button
													exactPermission={PERMISSION.INVITE}
													className="add-bonus-button invite-hcp text-nowrap"
													onClick={handleInviteButton}
													title={`Invite ${shift.resourceType.split('-')[0]}`}
													disabled={!openOnDemandSlotsCount}
												/>
											)}
										</div>
									)}
								</div>
							</div>
						)}
					</div>
				</Sticky>
			</div>
		</div>,

		<div className="col-sm-10 col-lg-10 col-xl-10 slots-container" key={`${shift.start_time}${shift.end_time}-slots`}>
			{sortSlots(shift.slots).map((slot) => (
				<ShiftSlot
					key={`${slot.slot_id}`}
					ref={(el) => {
						slotRefs[slot.slot_id] = el;
					}}
					slot={slot}
					shift={shift}
					isFixedMarkup={props.isFixedMarkup}
					setUnitAssignmentSlot={setUnitAssignmentSlot}
					units={units}
					updateSlotArrivalStatus={updateSlotArrivalStatus}
					onConfirm={onConfirm}
					onPrepareCancel={onPrepareCancel}
					user={user}
					onGuaranteedCheckboxChange={onGuaranteedCheckboxChange}
					onGuaranteeBundleChange={props.onGuaranteeBundleChange}
					setShowUnitAssignmentModal={setShowUnitAssignmentModal}
					preventGuaranteeOrBonusUpdates={preventGuaranteeOrBonusUpdates}
					handleEditBonusModalButton={handleEditBonusModalButton}
					handleAddBonusModalButton={handleAddBonusModalButton}
					assignCaregiver={handleAddFlexWorkerButton}
					routeSlotAction={handleRouteSlotButton}
					facilityFloatSlotAction={handleFacilityFloatSlotButton}
					shouldShowRouteBadges={shouldShowRouteBadges}
				/>
			))}
		</div>,

		user.hasUnitsEnabled && (
			<UnitAssignment
				key="unit-assignment"
				availableUnits={units}
				setIsLoading={props.setIsLoading}
				setShowModal={setShowUnitAssignmentModal}
				showModal={showUnitAssignmentModal}
				slot={unitAssignmentSlot}
				showOnDemandShiftTypeBadges={props.showOnDemandShiftTypeBadges}
				onFinish={() => dispatch(loadFeed())}
				isRecurring={props.data.isRecurring ?? false}
			/>
		),

		showEditWagePermissionModal && (
			<GenericPermissionsModal
				key="bonus-permission-modal"
				onConfirm={() => setShowEditWagePermissionModal(false)}
				onClose={() => setShowEditWagePermissionModal(false)}
				permissionAction=" edit healthcare professional hourly wages"
			/>
		),

		showEditWageModal && (
			<EditWageModal
				key="edit-wage-modal"
				shift={shift}
				openSlots={openOnDemandSlotsCount}
				isGuaranteed={isGuaranteed}
				onClose={() => setShowEditWageModal(false)}
				fetchData={props.fetchData}
			/>
		),

		showBonusPermissionsModal && (
			<GenericPermissionsModal
				key="bonus-permission-modal"
				onConfirm={() => setShowBonusPermissionsModal(false)}
				onClose={() => setShowBonusPermissionsModal(false)}
				permissionAction=" add, edit & remove bonuses"
				showProTip
			/>
		),

		showBonusModal && (
			<BonusModal
				key="bonus-modal"
				type={showBonusModal[0]}
				shift={showBonusModal[1]}
				previousBonus={shift.facilityPayableBonus}
				onConfirm={updateFacilityPayableBonus}
				onClose={() => setShowBonusModal(false)}
			/>
		),

		showConfirmConfirmation && (
			<ConfirmationModal
				key="confirm-confirmation"
				onConfirm={() => {
					setShowConfirmConfirmation(false);
					setShowConfirmShiftVendorWarningModal(true);
				}}
				onCancel={() => setShowConfirmConfirmation(false)}
			>
				Are you sure you want to confirm the shift?
			</ConfirmationModal>
		),

		showCancelFlex && (
			<CancelFlexShiftModal
				key="cancel-flex"
				onClose={() => setShowCancelFlex(false)}
				onCancel={handleFlexShiftCancel}
				resourceType={selectedSlotToCancel.resource_type}
				caregiverName={selectedSlotToCancel.caregiver_name}
				date={moment(selectedSlotToCancel.shift_date, 'MMMM Do').format('MMM. D')}
				day={moment(selectedSlotToCancel.shift_day, 'dddd').format('ddd.')}
				startTime={selectedSlotToCancel.start_time}
				endTime={selectedSlotToCancel.end_time}
				cancelModalErrorMessage={cancelModalErrorMessage}
				totalSlotsWorked={selectedSlotToCancel.caregiver?.caregiverStatsForFacility?.worked}
				isClaimedSlot={selectedSlotToCancel.caregiver_id !== null}
				isLoading={props.isLoading}
			/>
		),

		showRouteSlotModal && (
			<RouteSlotModal
				onClose={() => setShowRouteSlotModal(false)}
				setShowModal={showRouteSlotModal}
				onConfirm={() => OnRouteSlot(slotToRoute.slot)}
				isDailyFeed
				shiftToRoute={{
					shift: {
						facility_name: props.activeFacility.label,
						start_time: slotToRoute.slot.raw_start_time,
						facility_timezone: slotToRoute.slot.facility_timezone,
						formatted_start_time: slotToRoute.slot.start_time,
						formatted_end_time: slotToRoute.slot.end_time,
						resource_type: slotToRoute.slot.resource_type
					}
				}}
				badge={<ShiftTypeBadge shiftType="flex" />}
			/>
		),

		showCancelConfirmation && (
			<CancelShiftModal
				key="cancel-confirmation"
				onClose={() => onCloseCancelShiftModal(true)}
				onCancel={handleCancelShiftModalButton}
				isNonClaimedSlot
				isLoading={props.isLoading}
			/>
		),

		showCancelModal && (
			<CancelShiftModal
				key="cancel-shift-modal"
				onClose={() => onCloseCancelShiftModal(false)}
				onCancel={handleCancelShiftModalButton}
				resourceType={selectedSlotToCancel.resource_type}
				caregiverName={selectedSlotToCancel.caregiver_name}
				date={moment(selectedSlotToCancel.shift_date, 'MMMM Do').format('MMM. D')}
				day={moment(selectedSlotToCancel.shift_day, 'dddd').format('ddd.')}
				startTime={selectedSlotToCancel.start_time}
				endTime={selectedSlotToCancel.end_time}
				hourlyRate={selectedSlotToCancel.facility_price}
				timezone={selectedSlotToCancel.facility_timezone}
				bonus={selectedSlotToCancel.shift_facility_payable_bonus}
				isGuaranteed={selectedSlotToCancel.is_guaranteed}
				billableCancellationHours={currentFacility.facility_billable_cancellation_hours}
				windowCancellationHours={currentFacility.facility_window_cancellation_hours}
				startTimeAtUTC={selectedSlotToCancel.start_time_at_utc}
				cancelModalErrorMessage={cancelModalErrorMessage}
				checkinUberStatus={selectedSlotToCancel.checkin_uber_ride_status}
				totalSlotsWorked={selectedSlotToCancel.caregiver?.caregiverStatsForFacility?.worked}
				caregiverTier={selectedSlotToCancel.caregiver?.caregiverStats?.tier}
				isReturningWorker={selectedSlotToCancel.is_returning_worker}
				isNonClaimedSlot={false}
				isLoading={props.isLoading}
				isHybrid={selectedSlotToCancel.is_hybrid}
			/>
		),

		showGuaranteedModal && (
			<GuaranteeSlotsModal
				key="guaranteed-modal"
				slots={slotsToGuarantee}
				onSaveChanges={updateIsGuaranteedSlots}
				resourceType={shift.slots[0].resource_type}
				dateAndTime={`${shift.shift_date} from ${shift.start_time} - ${shift.end_time}`}
				onCancel={() => setShowGuaranteedModal(false)}
			/>
		),

		showSlotsModal && (
			<ConfirmationModal
				key="add-slot-modal"
				size="xl"
				title={getRequestSlotTitle()}
				onConfirm={currentRequestType === SHIFT_ROUTES.HYBRID ? requestHybridSlots : requestNewSlots}
				onCancel={hideSlotsModal}
				closeButton
				cancelLabel="Cancel"
				confirmLabel="Confirm"
				className="add-slot-modal"
			>
				<AddSlots
					numberOfSlots={numberOfSlots}
					isGlobalCalendar={!!props.fromGlobalOverview}
					allSlotsAreGuaranteed={allSlotsAreGuaranteed}
					setNumberOfSlots={setNumberOfSlots}
					shift={shiftSelectedToAddSlots}
				/>
			</ConfirmationModal>
		),

		showFlexSlotsModal && (
			<ConfirmationModal
				key="add-flex-slot-modal"
				size="xl"
				title="Add a new flex shift"
				onConfirm={requestNewFlexSlots}
				onCancel={hideFlexSlotsModal}
				cancelLabel="Cancel"
				confirmLabel="Confirm"
				className="add-slot-modal"
			>
				<AddSlots
					numberOfSlots={numberOfSlots}
					setNumberOfSlots={setNumberOfSlots}
					shift={flexShiftSelectedToAddSlots}
				/>
			</ConfirmationModal>
		),

		showInviteCaregiverModal && (
			<InviteCaregiverModal
				key="invite-caregiver-modal"
				size="xl"
				onCancel={handleCloseInviteCaregiverModal}
				onSendInvitation={handleSendInvitation}
				title={`Invite ${shift.resourceType}`}
				shift={shift}
				shiftId={onDemandShiftId}
				isRecurring={isRecurring}
				onShiftIdChange={setOnDemandShiftId}
				onRecurringStatusChange={setIsRecurring}
				closeButton
				cancelLabel="Cancel"
				backdropClassName="invite-caregiver-backdrop"
			/>
		),

		showAssignCaregiverModal && (
			<AssignCaregiverModal
				key="assign-caregiver_modal"
				onCancel={handleCloseAssignCaregiverModal}
				onAssign={handleOnAssign}
				shift={{ ...shift, ...slotToAssign, shiftsForAllDay }}
				backdropClassName="invite-caregiver-backdrop"
			/>
		),

		<VendorWarningModal
			key="add-slot-vendor-warning"
			isVendorEnabled={props.isVendorEnabled}
			showModal={showAddSlotVendorWarningModal}
			onConfirm={() => showModalToAddSlots(shift)}
			onCancel={() => setShowAddSlotVendorWarningModal(false)}
		/>,

		<VendorWarningModal
			key="confirm-shift-vendor-warning"
			isVendorEnabled={props.isVendorEnabled}
			showModal={showConfirmShiftVendorWarningModal}
			onConfirm={confirm}
			onCancel={() => setShowConfirmShiftVendorWarningModal(false)}
		/>,

		<VendorWarningModal
			key="cancel-shift-with-caregiver-vendor-warning"
			isVendorEnabled={props.isVendorEnabled}
			showModal={showCancelShiftWithCaregiverVendorWarningModal}
			onConfirm={() => {
				setShowCancelShiftWithCaregiverVendorWarningModal(false);
				setShowCancelModal(true);
			}}
			onCancel={() => setShowCancelShiftWithCaregiverVendorWarningModal(false)}
		/>,

		showConfirmModalContractCancellation && (
			<BaseModal
				titleIcon={<WarningIcon width={80} height={80} />}
				key="show-confirm-modal-contract-cancellation"
				confirmModal
				title="Please Confirm"
				primaryActionLabel="Continue"
				onPrimaryAction={() => onCancel(selectedSlotToCancel)}
				primaryActionDisabled={false}
				secondaryActionLabel="Keep Shift"
				onSecondaryAction={withdrawContractCancellation}
				secondaryActionDisabled={false}
				onHide={withdrawContractCancellation}
			>
				This shift is a part of{' '}
				<b>
					{selectedSlotToCancel && selectedSlotToCancel.contract_non_cancelled_slots_count
						? selectedSlotToCancel.contract_non_cancelled_slots_count
						: 0}
					-shift bundle
				</b>
				. Are you sure that you want to cancel the shift?
			</BaseModal>
		),

		showRefreshModal && (
			<SingleActionModal
				confirmModal
				key="show-refresh-modal"
				title="Refresh the Page"
				actionLabel="Refresh Now"
				onAction={() => {
					setShowRefreshModal(false);
					props.fetchData();
				}}
				className="refresh-modal"
				onHide={() => setShowRefreshModal(false)}
				disableClickOutside
			>
				The shift you selected has changed, refresh now to reflect the latest changes.
			</SingleActionModal>
		),

		showFacilityFloatModal && (
			<FacilityFloatModal
				key="facility-float-modal"
				onClose={() => setShowFacilityFloatModal(false)}
				onConfirm={onFacilityFloatSlot}
				shiftToFloat={slotToFloat}
				isFeed
			/>
		)
	];
};

export default memo(Shift);
